import React, { useState, useEffect, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, gql } from "@apollo/client";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";

import CycleSidebar from "./CycleSidebar";
import CyclesCapacityView from "./CyclesCapacityView";
import CyclesListingView from "./CyclesListingView";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";

import "../../starek.css";

const UI_STRINGS = {
    PAGE_TITLE: "Cycles",
    SEARCH_PLACEHOLDER: "Search",
    VIEW_MODE_CAPACITY: "Capacity View",
    VIEW_MODE_LIST: "List View",
    LABEL_NON_PRODUCTIVE: "Non-productive",
    LABEL_PRODUCTIVE: "Productive",
    ENTITY_SINGULAR: "Cycle",
    ENTITY_PLURAL: "Cycles",
    DELETE_TITLE_SINGLE: (name) => `Delete Cycle ${name}?`,
    DELETE_TITLE_BULK: (count) => `Delete ${count} Cycles?`,
    DELETE_MESSAGE_SINGLE: (name) => `You are about to delete cycle "${name}".

This action cannot be undone. All cycle stages associated with this cycle will also be deleted.

Are you sure you wish to continue?`,
    DELETE_MESSAGE_BULK: (
        count,
    ) => `You are about to delete ${count} cycle${count !== 1 ? "s" : ""}.

This action cannot be undone. All cycle stages associated with these cycles will also be deleted.

Are you sure you wish to continue?`,
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    TOTAL_SELECTED: (count) => `Total cycles selected: ${count}`,
};

const Cycles = () => {
    const starekContext = useContext(StarekContext);
    const [deleteDone, setDeleteDone] = useState(0);

    const GET_CYCLES_LIST_FOR_PLANT = gql`
        query GetCycles($plantId: Int!) {
            cycles(where: { plant_id: { equals: $plantId } }) {
                id
                name
                description
                created_at
                updated_at
                created_by {
                    id
                    name
                }
                updated_by {
                    id
                    name
                }
                cycle_stages {
                    id
                    name
                    is_productive
                    duration
                }
            }
        }
    `;

    const DELETE_ONE_CYCLE = gql`
        mutation deleteOneCycle($id: Int!) {
            deleteOneCycle(where: { id: $id }) {
                id
            }
        }
    `;

    const CREATE_ONE_CYCLE = gql`
        mutation CreateOneCycle($data: CycleCreateInput!) {
            createOneCycle(data: $data) {
                id
                name
                description
                created_at
            }
        }
    `;

    const UPDATE_ONE_CYCLE = gql`
        mutation UpdateOneCycle(
            $data: CycleUpdateInput!
            $where: CycleWhereUniqueInput!
        ) {
            updateOneCycle(data: $data, where: $where) {
                id
                name
                description
                updated_at
            }
        }
    `;

    const CREATE_ONE_CYCLE_STAGE = gql`
        mutation CreateOneCycleStage($data: CycleStageCreateInput!) {
            createOneCycleStage(data: $data) {
                id
                name
                duration
                is_productive
                description
            }
        }
    `;

    const UPDATE_ONE_CYCLE_STAGE = gql`
        mutation UpdateOneCycleStage(
            $data: CycleStageUpdateInput!
            $where: CycleStageWhereUniqueInput!
        ) {
            updateOneCycleStage(data: $data, where: $where) {
                id
                name
                duration
                is_productive
                description
            }
        }
    `;

    const DELETE_ONE_CYCLE_STAGE = gql`
        mutation DeleteOneCycleStage($where: CycleStageWhereUniqueInput!) {
            deleteOneCycleStage(where: $where) {
                id
            }
        }
    `;

    const { data, loading, refetch } = useQuery(GET_CYCLES_LIST_FOR_PLANT, {
        client: starekContext.engineClient,
        variables: { plantId: starekContext.lastUsedPlant.id },
    });

    const [deleteOneCycle] = useMutation(DELETE_ONE_CYCLE, {
        client: starekContext.engineClient,
    });
    const [createCycle] = useMutation(CREATE_ONE_CYCLE, {
        client: starekContext.engineClient,
    });
    const [updateCycle] = useMutation(UPDATE_ONE_CYCLE, {
        client: starekContext.engineClient,
    });
    const [createStage] = useMutation(CREATE_ONE_CYCLE_STAGE, {
        client: starekContext.engineClient,
    });
    const [updateStage] = useMutation(UPDATE_ONE_CYCLE_STAGE, {
        client: starekContext.engineClient,
    });
    const [deleteStage] = useMutation(DELETE_ONE_CYCLE_STAGE, {
        client: starekContext.engineClient,
    });

    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [viewMode, setViewMode] = useState("capacity"); // "capacity" or "list"
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [showAddCycleDrawer, setShowAddCycleDrawer] = useState(false);

    useEffect(() => {
        if (!data) return;
        setItems(data.cycles);
    }, [data]);

    const filteredItems = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (c) =>
                c.name?.toLowerCase().includes(q) ||
                c.description?.toLowerCase().includes(q),
        );
    }, [items, search]);

    const handleAdd = () => {
        if (selectedRows.length >= 1) {
            setSelectedRows([]);
        }
        setShowAddCycleDrawer(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        setShowAddCycleDrawer(true);
    };

    const handleDrawerClose = async () => {
        setShowAddCycleDrawer(false);
        if (selectedRows.length >= 1) {
            setSelectedRows([]);
        }
        await refetch();
    };

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleSaveCycle = async (cyclePayload) => {
        try {
            const currentUserId =
                starekContext?.currentUser?.id ||
                starekContext?.user?.id ||
                starekContext?.me?.id ||
                2;

            const isEdit = Boolean(cyclePayload.id);

            if (isEdit) {
                // Update cycle
                await updateCycle({
                    variables: {
                        where: { id: cyclePayload.id },
                        data: {
                            name: { set: cyclePayload.name },
                            description: {
                                set: cyclePayload.description || "",
                            },
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });

                // Handle stage updates/creates/deletes
                const toDelete = cyclePayload.deletedStageIds || [];
                const toUpdate = cyclePayload.stagesToUpdate || [];
                const toCreate = cyclePayload.stagesToCreate || [];

                for (const stageId of toDelete) {
                    await deleteStage({
                        variables: { where: { id: stageId } },
                    });
                }

                for (const stage of toUpdate) {
                    await updateStage({
                        variables: {
                            where: { id: stage.id },
                            data: {
                                name: { set: stage.name },
                                duration: { set: stage.duration },
                                defaultDuration: { set: stage.duration },
                                is_productive: { set: stage.is_productive },
                                description: { set: stage.description },
                                updated_by: {
                                    connect: { id: Number(currentUserId) },
                                },
                            },
                        },
                    });
                }

                for (const stage of toCreate) {
                    await createStage({
                        variables: {
                            data: {
                                name: stage.name,
                                duration: stage.duration,
                                defaultDuration: stage.duration,
                                is_productive: stage.is_productive,
                                description: stage.description,
                                cycle: { connect: { id: cyclePayload.id } },
                                created_by: {
                                    connect: { id: Number(currentUserId) },
                                },
                                updated_by: {
                                    connect: { id: Number(currentUserId) },
                                },
                            },
                        },
                    });
                }
            } else {
                // Create cycle
                const { data: createRes } = await createCycle({
                    variables: {
                        data: {
                            name: cyclePayload.name,
                            description: cyclePayload.description || "",
                            plant: {
                                connect: { id: starekContext.lastUsedPlant.id },
                            },
                            created_by: {
                                connect: { id: Number(currentUserId) },
                            },
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });

                const stages = cyclePayload.stages || [];
                for (const stage of stages) {
                    await createStage({
                        variables: {
                            data: {
                                name: stage.name,
                                duration: stage.duration,
                                defaultDuration: stage.duration,
                                is_productive: stage.is_productive,
                                description: stage.description,
                                cycle: {
                                    connect: {
                                        id: createRes.createOneCycle.id,
                                    },
                                },
                                created_by: {
                                    connect: { id: Number(currentUserId) },
                                },
                                updated_by: {
                                    connect: { id: Number(currentUserId) },
                                },
                            },
                        },
                    });
                }
            }

            await handleDrawerClose();
            starekContext.showSuccess(
                cyclePayload.id
                    ? `Cycle "${cyclePayload.name}" updated successfully`
                    : `Cycle "${cyclePayload.name}" created successfully`,
            );
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    const handleDeleteCycle = async (cycle) => {
        try {
            await deleteOneCycle({
                variables: { id: cycle.id },
            });
            await handleDrawerClose();
            starekContext.showSuccess(
                `Cycle "${cycle.name}" deleted successfully`,
            );
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    const handleDelete = (cycles) => {
        if (!cycles || cycles.length === 0 || isDeleting) return;

        const isSingle = cycles.length === 1;
        const title = isSingle
            ? UI_STRINGS.DELETE_TITLE_SINGLE(cycles[0].name)
            : UI_STRINGS.DELETE_TITLE_BULK(cycles.length);

        const message = isSingle
            ? UI_STRINGS.DELETE_MESSAGE_SINGLE(cycles[0].name)
            : UI_STRINGS.DELETE_MESSAGE_BULK(cycles.length);

        openConfirm({
            title: title,
            content: (
                <>
                    <div
                        className="text-dark"
                        style={{ whiteSpace: "pre-wrap" }}
                    >
                        {message}
                    </div>
                    <p className="mt-4 text-dark fw-bold">
                        {UI_STRINGS.TOTAL_SELECTED(cycles.length)}
                        &nbsp;
                        <span className="badge bg-info">{cycles.length}</span>
                    </p>
                </>
            ),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            type: "danger",
            onConfirm: async () => {
                setIsDeleting(true);
                setDeleteDone(0);

                try {
                    for (const cycle of cycles) {
                        try {
                            await deleteOneCycle({
                                variables: { id: cycle.id },
                            });
                        } catch (err) {
                            starekContext?.showError?.(err);
                        } finally {
                            setDeleteDone((d) => d + 1);
                        }
                    }
                    setSelectedRows([]);
                    await refetch();
                    starekContext.showSuccess(
                        `Successfully deleted ${cycles.length} cycle(s)`,
                    );
                } finally {
                    setIsDeleting(false);
                    closeConfirm();
                }
            },
            onClose: closeConfirm,
        });
    };

    return loading ? (
        <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ minHeight: "100vh" }}
        >
            <div className="text-xs d-flex justify-content-center">
                <i className="fa fa-recycle fa-10x text-success"></i>
            </div>
            <br />
            <Spinner />
        </div>
    ) : (
        <div className="container-fluid">
            {items && (
                <>
                    <div className="font-weight-bold mb-3">
                        {UI_STRINGS.PAGE_TITLE}
                    </div>

                    <EntityOperationBar
                        entity={{
                            singular: UI_STRINGS.ENTITY_SINGULAR,
                            plural: UI_STRINGS.ENTITY_PLURAL,
                        }}
                        allowSearch={true}
                        allowAdd={true}
                        allowDelete={true}
                        allowEdit={true}
                        selectedRows={selectedRows}
                        isDeleting={isDeleting}
                        deleteDone={deleteDone}
                        onSearch={(text) => setSearch(text)}
                        onAdd={handleAdd}
                        onViewChange={(mode) => {
                            setViewMode(mode);
                            setSelectedRows([]);
                        }}
                        onEdit={handleEdit}
                        onDelete={() => handleDelete(selectedRows)}
                    >
                        <EntityOperationBar.View
                            mode="capacity"
                            icon="fa fa-chart-bar"
                            tooltip={UI_STRINGS.VIEW_MODE_CAPACITY}
                        >
                            {/* Legend for Capacity View */}
                            <div className="d-flex align-items-center mb-3">
                                <div className="me-4">
                                    <span className="bg-danger rounded-circle px-9px me-2"></span>
                                    {UI_STRINGS.LABEL_NON_PRODUCTIVE}
                                </div>
                                <div>
                                    <span className="bg-success rounded-circle px-9px me-2"></span>
                                    {UI_STRINGS.LABEL_PRODUCTIVE}
                                </div>
                            </div>
                            <CyclesCapacityView
                                items={filteredItems}
                                selectedRows={selectedRows}
                                onSelectedRowsChange={({ selectedRows }) =>
                                    setSelectedRows(selectedRows)
                                }
                                filterText={search}
                            />
                        </EntityOperationBar.View>

                        <EntityOperationBar.View
                            mode="list"
                            icon="fa fa-list"
                            tooltip={UI_STRINGS.VIEW_MODE_LIST}
                        >
                            <CyclesListingView
                                items={filteredItems}
                                selectedRows={selectedRows}
                                onSelectedRowsChange={({ selectedRows }) =>
                                    setSelectedRows(selectedRows)
                                }
                                filterText={search}
                            />
                        </EntityOperationBar.View>
                    </EntityOperationBar>
                </>
            )}

            <CycleSidebar
                show={showAddCycleDrawer}
                onDiscard={handleDrawerClose}
                item={selectedRows[0] || null}
                plantId={starekContext.lastUsedPlant.id}
                onSave={handleSaveCycle}
                onDelete={handleDeleteCycle}
            />

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting}
                count={selectedRows.length}
                done={deleteDone}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            >
                {confirmCfg.content}
            </ConfirmModal>
        </div>
    );
};

export { Cycles };
